import type {
  Commitment,
  CommitmentState,
  PolicyEdit,
  SecondaryDocumentRef,
} from '@festina/life-general-types/life'
import {
  assertNotEmpty,
  notEmpty,
  type Optional,
  type ValidatedKey,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Select, TextInput } from '@festina-life/ui/forms'
import { useField } from 'formik'
import { useMemo, useState } from 'react'

import { useComponent } from '../../lenteHooks'

type Props = {
  fieldNamePrefix: ValidatedKey
  commitment: Commitment
  commitmentState: CommitmentState
}

// @beta None of the autogenerated fields are translated, so the ones here aren't either.
export const PolicyEdits = ({
  fieldNamePrefix,
  commitment,
  commitmentState,
}: Props) => {
  const PolicyEditComponent = useComponent(
    'pages.commitmentEditor.PolicyEditComponent',
  )
  assertNotEmpty(PolicyEditComponent)

  const [{ value }, , { setValue }] =
    useField<Optional<PolicyEdit>>(fieldNamePrefix)

  const [editType, setEditType] = useState<'create' | 'update' | 'delete'>(
    'update',
  )
  const [policyKey, setPolicyKey] = useState<string>()

  const policyKeys = useMemo(
    () =>
      commitmentState?.policyStates
        ?.map((policy) => policy?.policyKey)
        .filter(notEmpty) ?? [],
    [commitmentState?.policyStates],
  )

  const getInitialState = (): PolicyEdit => {
    switch (editType) {
      case 'create':
        return {
          _type: 'PolicyEdit',
          editType,
          policy: {
            _type: 'Policy',
            templateSourceRef: {
              _type: 'PolicyTemplate',
              secondaryId: policyKey,
            } as SecondaryDocumentRef,
          },
        }
      case 'update':
        return {
          _type: 'PolicyEdit',
          editType,
          policyState: commitmentState?.policyStates?.find(
            (policy) => policy?.policyKey === policyKey,
          ),
          policy: commitment?.policies?.find(
            (policy) => policy?.policyKey === policyKey,
          ),
        }
      case 'delete':
        return {
          _type: 'PolicyEdit',
          editType,
          policyKey,
        }
    }
  }

  const isSelectionConfirmed = notEmpty(value) && Object.keys(value).length > 0

  return (
    <>
      <Select
        wrapperProps={{
          // See beta-comment above; lack of translation is intentional
          label: 'Edit type',
        }}
        onChange={(editType) => setEditType(editType!)}
        value={editType}
        options={['create', 'update', 'delete'] as const}
        sort="automatic"
        disabled={isSelectionConfirmed}
      />
      {editType === 'create' ? (
        <TextInput
          wrapperProps={{
            // See beta-comment above; lack of translation is intentional
            label: 'Policy template id',
          }}
          onChange={(e) => setPolicyKey(e.target.value)}
          value={policyKey}
          disabled={isSelectionConfirmed}
        />
      ) : (
        <Select
          wrapperProps={{
            // See beta-comment above; lack of translation is intentional
            label: 'Policy key',
          }}
          onChange={setPolicyKey}
          value={policyKey}
          options={policyKeys}
          sort="automatic"
          disabled={isSelectionConfirmed}
        />
      )}
      {isSelectionConfirmed ? (
        <PolicyEditComponent fieldNamePrefix={fieldNamePrefix} />
      ) : (
        <Button
          onClick={() => setValue(getInitialState())}
          // See beta-comment above; lack of translation is intentional
        >
          Confirm
        </Button>
      )}
    </>
  )
}
